import React from 'react';
import { motion } from 'framer-motion';
import { TargetIcon, ChevronDownIcon, Settings2Icon, ActivityIcon, CrosshairIcon } from 'lucide-react';
export function Hero() {
  return <section className="relative min-h-screen flex flex-col items-center justify-center pt-24 pb-12 overflow-hidden bg-dark" data-id="element-92">
      {/* Tactical Background */}
      <div className="absolute inset-0 bg-circuit opacity-40" data-id="element-93"></div>
      <div className="absolute inset-0 bg-crosshair opacity-30" data-id="element-94"></div>

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-dark/80 to-dark" data-id="element-95"></div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center flex flex-col items-center w-full" data-id="element-96">
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.8,
        ease: 'easeOut'
      }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-panel text-primary text-sm font-medium mb-8" data-id="element-97">
          <TargetIcon className="w-4 h-4" data-id="element-98" />
          <span data-id="element-99">SISTEMA DE EJECUCIÓN MT5</span>
        </motion.div>

        <motion.h1 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.2
      }} className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-white leading-tight mb-6 uppercase tracking-tight" data-id="element-100">
          Deja de adivinar el mercado. <br className="hidden md:block" data-id="element-101" />
          <span className="text-primary text-glow" data-id="element-102">
            Empieza a ejecutar con precisión de Sniper.
          </span>
        </motion.h1>

        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.4
      }} className="text-lg sm:text-xl md:text-2xl text-gray-400 max-w-3xl mx-auto mb-10 font-sans leading-relaxed" data-id="element-103">
          La herramienta definitiva para MT5 que elimina el error humano,
          calcula tu riesgo en 1 segundo y domina la apertura de NY con el
          algoritmo Pandora Box.
        </motion.p>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.6
      }} className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16" data-id="element-104">
          <a href="#offer" className="w-full sm:w-auto px-8 py-4 bg-primary/10 text-primary border border-primary font-display font-bold text-lg rounded-sm hover:bg-primary hover:text-dark transition-all duration-300 box-glow uppercase tracking-wider flex items-center justify-center gap-2" data-id="element-105">
            Empieza Hoy
            <TargetIcon className="w-5 h-5" data-id="element-106" />
          </a>
        </motion.div>

        {/* MT5 Interface Mockup */}
        <motion.div initial={{
        opacity: 0,
        y: 40
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1,
        delay: 0.8
      }} className="w-full max-w-4xl glass-panel rounded-lg overflow-hidden shadow-2xl relative" data-id="element-107">
          {/* Mockup Header */}
          <div className="bg-surfaceHighlight/80 border-b border-primary/20 px-4 py-3 flex items-center justify-between" data-id="element-108">
            <div className="flex items-center gap-2 text-gray-300 text-sm font-display" data-id="element-109">
              <CrosshairIcon className="w-4 h-4 text-primary" data-id="element-110" />
              <span data-id="element-111">SNIPER PANEL PRO v2.0</span>
            </div>
            <div className="flex gap-2" data-id="element-112">
              <div className="w-3 h-3 rounded-full bg-danger/50" data-id="element-113"></div>
              <div className="w-3 h-3 rounded-full bg-secondary/50" data-id="element-114"></div>
              <div className="w-3 h-3 rounded-full bg-bullish/50" data-id="element-115"></div>
            </div>
          </div>

          {/* Mockup Body */}
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6 bg-dark/50" data-id="element-116">
            {/* Risk Settings */}
            <div className="col-span-1 space-y-4" data-id="element-117">
              <div className="glass-panel p-4 rounded-md" data-id="element-118">
                <div className="flex items-center justify-between mb-4 text-sm text-gray-400" data-id="element-119">
                  <span className="font-display" data-id="element-120">RISK MANAGEMENT</span>
                  <Settings2Icon className="w-4 h-4" data-id="element-121" />
                </div>
                <div className="space-y-3" data-id="element-122">
                  <div data-id="element-123">
                    <label className="text-xs text-gray-500 mb-1 block" data-id="element-124">
                      Risk %
                    </label>
                    <div className="bg-dark border border-primary/30 rounded px-3 py-2 text-primary font-mono text-lg text-glow" data-id="element-125">
                      1.00%
                    </div>
                  </div>
                  <div data-id="element-126">
                    <label className="text-xs text-gray-500 mb-1 block" data-id="element-127">
                      Account Balance
                    </label>
                    <div className="bg-dark border border-surfaceHighlight rounded px-3 py-2 text-white font-mono" data-id="element-128">
                      $100,000.00
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Execution Panel */}
            <div className="col-span-1 md:col-span-2 space-y-4" data-id="element-129">
              <div className="glass-panel p-4 rounded-md h-full flex flex-col justify-between" data-id="element-130">
                <div className="flex items-center justify-between mb-4 text-sm text-gray-400" data-id="element-131">
                  <span className="font-display" data-id="element-132">EXECUTION (US30)</span>
                  <ActivityIcon className="w-4 h-4 text-primary" data-id="element-133" />
                </div>

                <div className="grid grid-cols-2 gap-4 mb-6" data-id="element-134">
                  <div className="bg-dark border border-surfaceHighlight p-3 rounded text-center" data-id="element-135">
                    <span className="text-xs text-gray-500 block mb-1" data-id="element-136">
                      Lot Size
                    </span>
                    <span className="text-xl font-mono text-white" data-id="element-137">5.24</span>
                  </div>
                  <div className="bg-dark border border-surfaceHighlight p-3 rounded text-center" data-id="element-138">
                    <span className="text-xs text-gray-500 block mb-1" data-id="element-139">
                      Risk Amount
                    </span>
                    <span className="text-xl font-mono text-danger" data-id="element-140">
                      -$1,000.00
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4" data-id="element-141">
                  <button className="bg-danger/10 border border-danger text-danger py-3 rounded font-display font-bold hover:bg-danger hover:text-white transition-colors box-glow-danger" data-id="element-142">
                    SELL
                  </button>
                  <button className="bg-bullish/10 border border-bullish text-bullish py-3 rounded font-display font-bold hover:bg-bullish hover:text-dark transition-colors" style={{
                  boxShadow: '0 0 15px rgba(0, 255, 133, 0.2), inset 0 0 10px rgba(0, 255, 133, 0.1)'
                }} data-id="element-143">
                    BUY
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 1.5,
      duration: 1
    }} className="absolute bottom-6 left-1/2 -translate-x-1/2 animate-bounce" data-id="element-144">
        <ChevronDownIcon className="w-8 h-8 text-primary/50" data-id="element-145" />
      </motion.div>
    </section>;
}